import { ArrowLeft, Loader2, Search } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/catalyst/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import type { DestinationOption, DestinationScope } from '@/types/provider-destinations';

import { SCOPE_LABELS } from '../utils';
import { SearchResultItem } from './SearchResultItem';
import { useDestinationSearch } from './useDestinationSearch';

interface DestinationSearchPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  providerServiceId: string;
  scope: DestinationScope;
  providerName: string;
  providerCity?: string;
  onSelectResult: (contactPoint: DestinationOption) => void;
  mode?: 'sheet' | 'inline';
  onBack?: () => void;
}

export const DestinationSearchPanel = ({
  open,
  onOpenChange,
  providerServiceId,
  scope,
  providerName,
  providerCity,
  onSelectResult,
  mode = 'sheet',
  onBack,
}: DestinationSearchPanelProps) => {
  const defaultQuery = [providerName, providerCity]
    .filter(Boolean)
    .join(', ');
  const [query, setQuery] = useState(defaultQuery);
  const [includeWeb, setIncludeWeb] = useState(true);

  const { search, results, isSearching, hasSearched, reset } =
    useDestinationSearch(providerServiceId, scope);

  // Reset state when the panel opens
  useEffect(() => {
    if (open) {
      setQuery(defaultQuery);
      reset();
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = useCallback(() => {
    search(query, includeWeb);
  }, [search, query, includeWeb]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleSearch();
      }
    },
    [handleSearch],
  );

  const internalResults = results?.results ?? [];
  const webSuggestions = results?.web_suggestions ?? [];
  const webResults = results?.web_results ?? [];
  const hasResults =
    internalResults.length > 0 ||
    webSuggestions.length > 0 ||
    webResults.length > 0;
  const includeWebCheckboxId = `include-web-search-${mode}-${scope}`;

  const content = (
    <div className="flex flex-col gap-4">
      {mode === 'inline' && (
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            size="sm"
            className="gap-1"
            onClick={() => onBack?.()}
          >
            <ArrowLeft className="size-3.5" />
            Back
          </Button>
          <div className="text-sm font-medium text-zinc-700">
            Search destinations &mdash; {SCOPE_LABELS[scope]}
          </div>
        </div>
      )}

      {/* Search input */}
      <div className="flex items-center gap-2">
        <div className="flex-1">
          <Input
            type="search"
            value={query}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setQuery(e.target.value)
            }
            onKeyDown={handleKeyDown}
            placeholder="Provider name, city, fax number..."
          />
        </div>
        <Button
          onClick={handleSearch}
          disabled={isSearching}
          size="sm"
        >
          {isSearching ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <Search className="size-4" />
          )}
        </Button>
      </div>

      {/* Include web toggle */}
      <div className="flex items-center gap-2">
        <Checkbox
          id={includeWebCheckboxId}
          checked={includeWeb}
          onCheckedChange={(v) => setIncludeWeb(v === true)}
        />
        <Label
          htmlFor={includeWebCheckboxId}
          className="text-sm text-zinc-600 cursor-pointer"
        >
          Include web search
        </Label>
      </div>

      {includeWeb && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          Web search may take about 30 seconds to return results.
        </div>
      )}

      {/* Results */}
      <div className="flex-1 space-y-4 overflow-y-auto">
        {isSearching && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="size-5 animate-spin text-zinc-400" />
          </div>
        )}

        {!isSearching && hasSearched && !hasResults && (
          <p className="py-6 text-center text-sm text-zinc-500">
            No results found. Try a different query or enter a destination manually.
          </p>
        )}

        {!isSearching && internalResults.length > 0 && (
          <div>
            <p className="mb-2 text-xs font-medium uppercase tracking-wide text-zinc-500">
              Internal matches
            </p>
            <div className="space-y-2">
              {internalResults.map((r) => (
                <SearchResultItem
                  key={r.contact_point_id}
                  result={r}
                  onSelect={onSelectResult}
                />
              ))}
            </div>
          </div>
        )}

        {!isSearching && webSuggestions.length > 0 && (
          <div>
            <p className="mb-2 text-xs font-medium uppercase tracking-wide text-zinc-500">
              Web suggestions
            </p>
            <div className="space-y-2">
              {webSuggestions.map((r) => (
                <SearchResultItem
                  key={r.contact_point_id}
                  result={r}
                  onSelect={onSelectResult}
                />
              ))}
            </div>
          </div>
        )}

        {!isSearching && includeWeb && webResults.length > 0 && (
          <div>
            <p className="mb-2 text-xs font-medium uppercase tracking-wide text-zinc-500">
              Web results
            </p>
            <div className="space-y-3">
              {webResults.map((result) => (
                <div
                  key={result.url}
                  className="rounded-lg border border-zinc-200 bg-zinc-50 p-3"
                >
                  <div className="text-sm font-medium text-zinc-900">
                    {result.title || result.url}
                  </div>
                  <a
                    href={result.url}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-1 block truncate text-xs text-blue-600 hover:underline"
                  >
                    {result.url}
                  </a>
                  {result.excerpts && result.excerpts.length > 0 && (
                    <p className="mt-2 line-clamp-2 text-xs text-zinc-600">
                      {result.excerpts[0]}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  if (mode === 'inline') {
    return (
      <div className="rounded-lg border border-zinc-200 bg-white p-4">
        {content}
      </div>
    );
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-lg">
        <SheetHeader>
          <SheetTitle>
            Search destinations &mdash; {SCOPE_LABELS[scope]}
          </SheetTitle>
        </SheetHeader>
        <div className="px-4 pb-4">{content}</div>
      </SheetContent>
    </Sheet>
  );
};
